import { useTranslation } from 'react-i18next';

interface ChakraData {
  id: number;
  zhName: string;
  enName: string;
  sanskrit: string;
  color: string;
  bgColor: string;
  location: string;
  keywords: string;
  topPct: number;
  side: 'left' | 'right';
  element: string;
}

const CHAKRAS: ChakraData[] = [
  {
    id: 7,
    zhName: '顶轮',
    enName: 'Crown Chakra',
    sanskrit: 'Sahasrara',
    color: '#c084fc',
    bgColor: 'rgba(192, 132, 252, 0.18)',
    location: '头顶',
    keywords: '灵性 · 宇宙意识',
    element: '意识',
    topPct: 4,
    side: 'left',
  },
  {
    id: 6,
    zhName: '眉心轮',
    enName: 'Third Eye',
    sanskrit: 'Ajna',
    color: '#818cf8',
    bgColor: 'rgba(129, 140, 248, 0.18)',
    location: '两眉之间',
    keywords: '直觉 · 洞察力',
    element: '光',
    topPct: 21,
    side: 'right',
  },
  {
    id: 5,
    zhName: '喉轮',
    enName: 'Throat Chakra',
    sanskrit: 'Vishuddha',
    color: '#38bdf8',
    bgColor: 'rgba(56, 189, 248, 0.18)',
    location: '喉咙',
    keywords: '表达 · 沟通',
    element: '空间',
    topPct: 34,
    side: 'left',
  },
  {
    id: 4,
    zhName: '心轮',
    enName: 'Heart Chakra',
    sanskrit: 'Anahata',
    color: '#4ade80',
    bgColor: 'rgba(74, 222, 128, 0.18)',
    location: '胸口中央',
    keywords: '爱 · 慈悲',
    element: '风',
    topPct: 46,
    side: 'right',
  },
  {
    id: 3,
    zhName: '太阳神经丛',
    enName: 'Solar Plexus',
    sanskrit: 'Manipura',
    color: '#fbbf24',
    bgColor: 'rgba(251, 191, 36, 0.18)',
    location: '上腹部',
    keywords: '意志 · 自信',
    element: '火',
    topPct: 57,
    side: 'left',
  },
  {
    id: 2,
    zhName: '脐轮',
    enName: 'Sacral Chakra',
    sanskrit: 'Svadhisthana',
    color: '#fb923c',
    bgColor: 'rgba(249, 115, 22, 0.18)',
    location: '下腹部',
    keywords: '创造力 · 情感',
    element: '水',
    topPct: 68,
    side: 'right',
  },
  {
    id: 1,
    zhName: '海底轮',
    enName: 'Root Chakra',
    sanskrit: 'Muladhara',
    color: '#f87171',
    bgColor: 'rgba(248, 113, 113, 0.18)',
    location: '脊柱底部',
    keywords: '根基 · 安全感',
    element: '土',
    topPct: 78,
    side: 'left',
  },
];

// Spine gradient stops for the vertical energy line
const SPINE_GRADIENT = 'linear-gradient(to bottom, #c084fc, #818cf8, #38bdf8, #4ade80, #fbbf24, #fb923c, #f87171)';

export default function ChakraDiagram() {
  const { i18n } = useTranslation('common');
  const isEn = i18n.language === 'en';

  const leftChakras = CHAKRAS.filter((c) => c.side === 'left');
  const rightChakras = CHAKRAS.filter((c) => c.side === 'right');

  return (
    <section className="py-16 md:py-24 overflow-hidden" style={{ background: 'linear-gradient(180deg, #080614 0%, #0e0a22 40%, #100c28 70%, #080614 100%)' }}>
      {/* Top golden divider */}
      <div className="w-full h-px mb-0" style={{ background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.25), transparent)' }} />

      <div className="max-w-6xl mx-auto px-6">
        {/* Title */}
        <div className="text-center mb-10 md:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-5" style={{ border: '1px solid rgba(212,175,55,0.35)', background: 'rgba(212,175,55,0.08)' }}>
            <i className="ri-sparkling-2-line text-yellow-300/80 text-sm"></i>
            <span className="text-yellow-300/80 text-xs tracking-[0.25em] font-medium">CHAKRA MAP</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3 tracking-wide">
            {isEn ? 'Human Body Chakra Chart' : '人体脉轮位置图'}
          </h2>
          <p className="text-slate-400 text-sm tracking-wider">
            {isEn ? 'Seven Energy Centres — From Root to Crown' : '七大能量中心 · 从海底轮到顶轮'}
          </p>
          {/* Ornamental line */}
          <div className="flex items-center justify-center gap-3 mt-5">
            <div className="h-px w-16" style={{ background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.4))' }} />
            <i className="ri-flower-line text-yellow-400/40 text-sm"></i>
            <div className="h-px w-16" style={{ background: 'linear-gradient(90deg, rgba(212,175,55,0.4), transparent)' }} />
          </div>
        </div>

        {/* ─── Desktop Diagram ─── */}
        <div className="hidden md:block relative mx-auto" style={{ height: '760px', maxWidth: '960px' }}>

          {/* Ambient background radial glow */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none" style={{ width: '420px', height: '640px', background: 'radial-gradient(ellipse, rgba(139,92,246,0.10) 0%, rgba(192,132,252,0.04) 50%, transparent 75%)', borderRadius: '50%' }} />

          {/* Body Image + Spine Energy Line + Chakra Dots */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 h-full" style={{ width: '200px' }}>
            <img
              src="https://static.readdy.ai/image/705f25322c737a89d377410a2840897b/f4d5576319443785cc5680bc072f6794.png"
              alt={isEn ? 'Seated Meditation Chakra Body' : '打坐冥想人体脉轮图'}
              className="w-full h-full object-cover object-top"
              style={{ borderRadius: '12px' }}
            />

            {/* Spine energy gradient line */}
            <div
              className="absolute left-1/2 -translate-x-px pointer-events-none"
              style={{
                top: `${CHAKRAS[CHAKRAS.length - 1].topPct}%`,
                height: `${CHAKRAS[0].topPct - CHAKRAS[CHAKRAS.length - 1].topPct + 2}%`,
                width: '2px',
                background: SPINE_GRADIENT,
                opacity: 0.5,
                transform: 'translateX(-50%) scaleY(-1)',
                filter: 'blur(1px)',
              }}
            />

            {/* Chakra Dots on Body */}
            {CHAKRAS.map((chakra, idx) => (
              <div
                key={chakra.id}
                className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2"
                style={{ top: `${chakra.topPct}%`, animationDelay: `${idx * 0.18}s` }}
              >
                {/* Outer slow pulse ring */}
                <div
                  className="absolute rounded-full animate-ping"
                  style={{
                    width: '30px',
                    height: '30px',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%,-50%)',
                    backgroundColor: chakra.color,
                    opacity: 0.25,
                    animationDuration: '2.4s',
                  }}
                />
                {/* Glow halo */}
                <div
                  className="absolute rounded-full"
                  style={{
                    width: '26px',
                    height: '26px',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%,-50%)',
                    backgroundColor: chakra.color,
                    opacity: 0.22,
                    filter: 'blur(6px)',
                  }}
                />
                {/* Core dot */}
                <div
                  className="relative w-5 h-5 rounded-full border-2 border-white/70 z-10 cursor-pointer"
                  style={{
                    backgroundColor: chakra.color,
                    boxShadow: `0 0 12px ${chakra.color}, 0 0 28px ${chakra.color}88, 0 0 48px ${chakra.color}33`,
                  }}
                />
              </div>
            ))}
          </div>

          {/* ─── Left Labels ─── */}
          {leftChakras.map((chakra) => (
            <div
              key={chakra.id}
              className="absolute -translate-y-1/2 flex items-center"
              style={{ top: `${chakra.topPct}%`, right: 'calc(50% + 110px)' }}
            >
              {/* Label Card */}
              <div
                className="text-right rounded-2xl px-4 py-3 mr-4 backdrop-blur-sm"
                style={{
                  background: `linear-gradient(135deg, ${chakra.bgColor}, rgba(10,8,24,0.6))`,
                  border: `1px solid ${chakra.color}50`,
                  minWidth: '160px',
                  boxShadow: `0 4px 24px ${chakra.color}18`,
                }}
              >
                <p className="text-[10px] tracking-[0.18em] italic mb-0.5 font-light" style={{ color: chakra.color }}>
                  {chakra.sanskrit}
                </p>
                <p className="font-bold text-white text-sm leading-tight">
                  {isEn ? chakra.enName : chakra.zhName}
                </p>
                {!isEn && (
                  <p className="text-slate-400 text-[11px] mt-0.5">{chakra.enName}</p>
                )}
                <div className="flex items-center justify-end gap-1.5 mt-1.5">
                  <div className="w-4 h-4 flex items-center justify-center" style={{ color: `${chakra.color}99` }}>
                    <i className="ri-map-pin-line text-xs"></i>
                  </div>
                  <span className="text-slate-400 text-[10px]">{chakra.location}</span>
                </div>
                <p className="text-xs mt-1 font-medium tracking-wide" style={{ color: chakra.color }}>
                  {chakra.keywords}
                </p>
                <span
                  className="inline-flex items-center gap-1 text-[9px] px-2 py-0.5 rounded-full mt-1.5 font-medium tracking-wider"
                  style={{ background: `${chakra.color}20`, color: chakra.color, border: `1px solid ${chakra.color}30` }}
                >
                  {chakra.element}
                </span>
              </div>
              {/* Connector line with gradient */}
              <div
                className="flex-shrink-0 h-px"
                style={{ width: '44px', background: `linear-gradient(90deg, transparent, ${chakra.color})`, opacity: 0.7 }}
              />
              {/* Connector dot */}
              <div
                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: chakra.color, boxShadow: `0 0 6px ${chakra.color}`, opacity: 0.9 }}
              />
            </div>
          ))}

          {/* ─── Right Labels ─── */}
          {rightChakras.map((chakra) => (
            <div
              key={chakra.id}
              className="absolute -translate-y-1/2 flex items-center"
              style={{ top: `${chakra.topPct}%`, left: 'calc(50% + 110px)' }}
            >
              {/* Connector dot */}
              <div
                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: chakra.color, boxShadow: `0 0 6px ${chakra.color}`, opacity: 0.9 }}
              />
              {/* Connector line with gradient */}
              <div
                className="flex-shrink-0 h-px"
                style={{ width: '44px', background: `linear-gradient(90deg, ${chakra.color}, transparent)`, opacity: 0.7 }}
              />
              {/* Label Card */}
              <div
                className="text-left rounded-2xl px-4 py-3 ml-4 backdrop-blur-sm"
                style={{
                  background: `linear-gradient(135deg, rgba(10,8,24,0.6), ${chakra.bgColor})`,
                  border: `1px solid ${chakra.color}50`,
                  minWidth: '160px',
                  boxShadow: `0 4px 24px ${chakra.color}18`,
                }}
              >
                <p className="text-[10px] tracking-[0.18em] italic mb-0.5 font-light" style={{ color: chakra.color }}>
                  {chakra.sanskrit}
                </p>
                <p className="font-bold text-white text-sm leading-tight">
                  {isEn ? chakra.enName : chakra.zhName}
                </p>
                {!isEn && (
                  <p className="text-slate-400 text-[11px] mt-0.5">{chakra.enName}</p>
                )}
                <div className="flex items-center gap-1.5 mt-1.5">
                  <div className="w-4 h-4 flex items-center justify-center" style={{ color: `${chakra.color}99` }}>
                    <i className="ri-map-pin-line text-xs"></i>
                  </div>
                  <span className="text-slate-400 text-[10px]">{chakra.location}</span>
                </div>
                <p className="text-xs mt-1 font-medium tracking-wide" style={{ color: chakra.color }}>
                  {chakra.keywords}
                </p>
                <span
                  className="inline-flex items-center gap-1 text-[9px] px-2 py-0.5 rounded-full mt-1.5 font-medium tracking-wider"
                  style={{ background: `${chakra.color}20`, color: chakra.color, border: `1px solid ${chakra.color}30` }}
                >
                  {chakra.element}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* ─── Mobile: compact list ─── */}
        <div className="md:hidden">
          <div className="w-40 mx-auto mb-8 relative">
            <img
              src="https://static.readdy.ai/image/705f25322c737a89d377410a2840897b/f4d5576319443785cc5680bc072f6794.png"
              alt={isEn ? 'Seated Meditation Chakra Body' : '打坐冥想人体脉轮图'}
              className="w-full rounded-xl"
            />
            {CHAKRAS.map((chakra) => (
              <div
                key={chakra.id}
                className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2"
                style={{ top: `${chakra.topPct}%` }}
              >
                <div
                  className="w-4 h-4 rounded-full border-2 border-white/70"
                  style={{
                    backgroundColor: chakra.color,
                    boxShadow: `0 0 10px ${chakra.color}, 0 0 20px ${chakra.color}55`,
                  }}
                />
              </div>
            ))}
          </div>

          <div className="space-y-2.5">
            {CHAKRAS.map((chakra) => (
              <div
                key={chakra.id}
                className="flex items-start gap-3 rounded-xl px-4 py-3"
                style={{
                  background: `linear-gradient(135deg, ${chakra.bgColor}, rgba(10,8,24,0.5))`,
                  border: `1px solid ${chakra.color}40`,
                }}
              >
                <div
                  className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center border-2 border-white/40 mt-0.5"
                  style={{ backgroundColor: chakra.color, boxShadow: `0 0 12px ${chakra.color}88` }}
                >
                  <span className="text-white text-xs font-bold">{chakra.id}</span>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-white text-sm">{isEn ? chakra.enName : chakra.zhName}</span>
                    <span className="text-[10px] italic tracking-wider" style={{ color: chakra.color }}>{chakra.sanskrit}</span>
                  </div>
                  <p className="text-slate-400 text-xs mt-0.5">{chakra.location} · {chakra.element}</p>
                  <p className="text-xs mt-0.5" style={{ color: chakra.color }}>{chakra.keywords}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ─── Color Legend ─── */}
        <div className="mt-12 md:mt-16">
          {/* Divider */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="h-px flex-1" style={{ background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.2))' }} />
            <i className="ri-seedling-line text-yellow-400/30 text-sm"></i>
            <div className="h-px flex-1" style={{ background: 'linear-gradient(90deg, rgba(212,175,55,0.2), transparent)' }} />
          </div>
          <div className="flex flex-wrap justify-center gap-x-5 gap-y-3">
            {CHAKRAS.map((chakra) => (
              <div key={chakra.id} className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: chakra.color, boxShadow: `0 0 8px ${chakra.color}` }}
                />
                <span className="text-slate-400 text-xs whitespace-nowrap">
                  {isEn ? chakra.enName : `${chakra.zhName} · ${chakra.enName}`}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom golden divider */}
      <div className="w-full h-px mt-16" style={{ background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.25), transparent)' }} />
    </section>
  );
}
